import { s } from '@/theme/tokens';
import { PageHeader } from '@/ui/layout/PageHeader';
import { Box } from '@/ui/primitives';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import type { ReactElement, ReactNode } from 'react';

export type SettingsTabbedHeaderTabDefinition<TTab extends string> = {
  value: TTab;
  label: ReactNode;
};

type SettingsTabbedHeaderProps<TTab extends string> = {
  title: string;
  subtitle: string;
  activeTab: TTab;
  tabs: ReadonlyArray<SettingsTabbedHeaderTabDefinition<TTab>>;
  tabsAriaLabel: string;
  onTabChange: (nextValue: TTab) => void;
};

export const SettingsTabbedHeader = <TTab extends string>({
  title,
  subtitle,
  activeTab,
  tabs,
  tabsAriaLabel,
  onTabChange,
}: SettingsTabbedHeaderProps<TTab>): ReactElement => {
  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title={title} subtitle={subtitle} align="start" />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: s('lg'), mb: '8px' }}>
        <Tabs
          value={activeTab}
          onChange={(_event, nextValue) => {
            if (typeof nextValue === 'string') {
              onTabChange(nextValue as TTab);
            }
          }}
          preserveScrollOnChange={false}
          aria-label={tabsAriaLabel}
          sx={{
            px: 0,
            minHeight: '34px !important',
            '& .MuiTab-root': {
              fontSize: '1rem',
              fontWeight: 500,
              minHeight: '36px !important',
              height: '36px !important',
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              sx={{
                minHeight: '36px !important',
                height: '36px !important',
                paddingTop: 0,
                paddingBottom: '8px',
              }}
            />
          ))}
        </Tabs>
      </Box>
    </Box>
  );
};
